import { useState } from "react";
import {
  CurveType,
  LineChart as GiftedLineChart,
  lineDataItem,
  yAxisSides,
} from "react-gifted-charts";
import { XStack, YStack, Text, Token, getTokenValue, View } from "tamagui";

const Y_AXIS_LABEL_GAP = 8;
const Y_AXIS_OFFSET_DELTA = 1;

export type LineChartDataItem = lineDataItem;

export interface LineChartProps {
  data: LineChartDataItem[];
  unit: string;
  minValue?: number;
  maxValue?: number;
  chartHeight?: number;
  noOfSections?: number;
  yAxisLabelWidth?: number;
  yAxisLabelDecimals?: number;
  color?: string;
  lineGradientStartColor?: string;
  lineGradientEndColor?: string;
  startFillColor?: string;
  endFillColor?: string;
}

export const LineChart = (props: LineChartProps) => {
  const [parentWidth, setParentWidth] = useState(0);
  const axisTextColor = getTokenValue("$textLight" as Token, "color");
  const defaultGradientStartColor = getTokenValue(
    "$hgPrimaryGradientStart" as Token,
    "color",
  );
  const defaultGradientEndColor = getTokenValue(
    "$hgPrimaryGradientEnd" as Token,
    "color",
  );
  const rulesColor = getTokenValue("$white6" as Token, "color");
  const yAxisLabelWidth = props.yAxisLabelWidth ?? 35;
  const noOfSections = props.noOfSections ?? 2;

  return (
    <YStack
      flex={1}
      gap="$2"
      onLayout={({ nativeEvent }) => setParentWidth(nativeEvent.layout.width)}
    >
      <GiftedLineChart
        width={parentWidth - yAxisLabelWidth - Y_AXIS_LABEL_GAP}
        height={props.chartHeight}
        adjustToWidth
        data={props.data.map(({ value }) => ({ value }))}
        curved
        areaChart
        hideDataPoints
        curveType={CurveType.QUADRATIC}
        startOpacity={0.3}
        endOpacity={0}
        pointerConfig={{ showPointerStrip: false, hidePointers: true }}
        thickness={4}
        initialSpacing={0}
        endSpacing={0}
        color={props.color || defaultGradientStartColor}
        lineGradientStartColor={
          props.lineGradientStartColor || defaultGradientStartColor
        }
        startFillColor={props.startFillColor || defaultGradientStartColor}
        lineGradientEndColor={
          props.lineGradientEndColor || defaultGradientEndColor
        }
        endFillColor={props.endFillColor || defaultGradientEndColor}
        yAxisTextStyle={{
          color: axisTextColor,
          fontSize: 14,
          paddingLeft: Y_AXIS_LABEL_GAP,
        }}
        xAxisLabelsHeight={0}
        yAxisSide={yAxisSides.RIGHT}
        yAxisLabelSuffix={props.unit}
        yAxisLabelWidth={yAxisLabelWidth}
        yAxisLabelContainerStyle={{
          justifyContent: "flex-start",
        }}
        yAxisOffset={
          props.minValue !== undefined
            ? props.minValue
            : Math.min(
                ...props.data.map(({ value }) => Math.round(value ?? 0)),
              ) - Y_AXIS_OFFSET_DELTA
        }
        roundToDigits={props.yAxisLabelDecimals ?? 1}
        maxValue={
          props.maxValue !== undefined
            ? props.maxValue
            : Math.max(...props.data.map(({ value }) => Math.round(value ?? 0)))
        }
        rulesColor={rulesColor}
        xAxisColor={rulesColor}
        yAxisColor="transparent"
        rulesType="solid"
        noOfSections={noOfSections}
        backgroundColor="transparent"
      />
      <XStack
        width={parentWidth - yAxisLabelWidth - Y_AXIS_LABEL_GAP}
        justifyContent="space-between"
      >
        {props.data.map((value, index) => (
          <View key={index} flexGrow={1} flexBasis={0} alignItems="center">
            <Text fontSize={14} color={axisTextColor}>
              {value.label}
            </Text>
          </View>
        ))}
      </XStack>
    </YStack>
  );
};
